<template>
  <div class="signup-complete-section">
    <h3 class="signup-complete-section__title">
      {{ $tc('SignupPage.SignupCompletePage.title') }}
    </h3>

    <p
      class="signup-complete-section__message"
      v-html="$tc('SignupPage.SignupCompletePage.contentText')"
    ></p>

    <attention-message-section
      v-if="isShowAttentionMessage"
      class="signup-complete-section__attention-message"
      :attentionMessage="attentionMessage"
    />

    <payment-schedule-section
      v-if="paymentSchedule"
      class="signup-complete-section__payment-schedule"
      :paymentSchedule="paymentSchedule"
    />

    <div v-if="!isApp" class="signup-complete-section__apps-link">
      <h4 class="signup-complete-section__apps-link__title">
        {{ $tc('SignupPage.SignupCompletePage.app.title') }}
      </h4>

      <ul class="signup-complete-section__apps-link__list">
        <li class="signup-complete-section__apps-link__google">
          <a
            href="https://play.google.com/store/apps/details?id=net.superformula.sfgo&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
            target="_blank"
          >
            <img
              src="https://play.google.com/intl/ja/badges/static/images/badges/ja_badge_web_generic.png"
              :alt="$tc('SignupPage.SignupCompletePage.app.googleAlt')"
            />
          </a>
        </li>
        <li class="signup-complete-section__apps-link__app-store">
          <a
            href="https://apps.apple.com/us/app/sfgo/id1615551217?itsct=apps_box_badge&amp;itscg=30200"
            target="_blank"
          >
            <img
              src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/ja-jp?size=250x83&amp;releaseDate=1648598400&h=4caa447b276cdbdc2dfad2ec801d220f"
              :alt="$tc('SignupPage.SignupCompletePage.app.appStoreAlt')"
            />
          </a>
        </li>
      </ul>
    </div>
    <p class="signup-complete-section__back-link">
      <a
        v-if="isApp"
        href="javascript:void(0)"
        class="atom-router-btn atom-router-btn--light--secondary atom-router-btn--large"
        @click="onMoveToLogin()"
        >{{ $tc('common.login') }}</a
      >
      <a
        v-else
        :href="sfgoUrl"
        class="atom-router-btn atom-router-btn--light--secondary atom-router-btn--large"
        >{{ $tc('common.login') }}</a
      >
    </p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import AttentionMessageSection, {
  AttentionMessageData,
} from '@/components/pc/MyPage/common/AttentionMessageSection.vue'
import PaymentScheduleSection, {
  PaymentScheduleData,
} from '@/components/pc/MyPage/common/PaymentScheduleSection.vue'
import DeviceInfo from '@/util/DeviceInfo'

/**
 * 新規会員登録:会員登録完了画面セクションのコンポーネント
 */
export default defineComponent({
  name: 'SignupCompleteSection',
  components: {
    AttentionMessageSection,
    PaymentScheduleSection,
  },
  props: {
    /**
     * クーポンを使用してるか
     */
    isCouponUsed: {
      type: Boolean,
      default: false,
    },
    /**
     * 注意メッセージ
     */
    attentionMessage: {
      type: Object as PropType<AttentionMessageData>,
      default: null,
    },
    /**
     * 請求予定情報
     */
    paymentSchedule: {
      type: Array as PropType<PaymentScheduleData[]>,
      default: null,
    },
  },
  setup(props, { emit }) {
    // アプリかどうか
    const isApp = computed(() => DeviceInfo.isCordova())

    /**
     * 注意メッセージを表示するかどうか
     * 注意メッセージが存在する かつ 請求予定が存在する場合（月額プランを登録した場合に表示される）
     * クーポンプランを登録 かつ 注意メッセージが存在する場合
     */
    const isShowAttentionMessage = computed(
      () =>
        (props.attentionMessage && props.paymentSchedule) ||
        (props.attentionMessage && props.isCouponUsed),
    )

    /**
     * ログイン画面に遷移
     */
    const onMoveToLogin = () => {
      emit('onMoveToLogin')
    }

    return {
      isApp,
      sfgoUrl: `${process.env.VUE_APP_BROWSER_CAN_REDIRECT_TO_APP_BASE_URL}#/login`,
      isShowAttentionMessage,
      onMoveToLogin,
    }
  },
})
</script>

<style lang="scss" scoped>
.signup-complete-section {
  padding: 80px 0;
  font-feature-settings: 'palt' on;
  letter-spacing: 0.04em;

  &__title {
    @include fontSetTextMedium(17);

    line-height: 1.41;
    font-weight: 500;
  }

  &__message {
    margin: 24px 0;
    padding: 0 16px;
    @include fontSetTextMedium();
  }

  &__attention-message,
  &__payment-schedule {
    margin-top: 24px;
  }

  &__payment-schedule {
    margin-bottom: 24px;
  }

  &__apps-link {
    width: min(100%, 800px);
    padding: 24px 0 20px;
    margin: 24px auto;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    &__title {
      margin-bottom: 16px;
      @include fontSetTextMedium(17);

      text-align: center;
      line-height: 1.41;
    }

    &__list {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    &__google {
      width: 132px;
    }

    &__app-store {
      width: 115px;
    }
  }

  &__back-link {
    padding: 0 16px;

    ::v-deep {
      .atom-router-btn {
        max-width: 345px;
        margin: 0 auto;
      }
    }
  }
}
</style>

<style lang="scss">
.signup-complete-section {
  &__message {
    > * + * {
      margin-top: 1.5em;
    }
  }
}

.atom-router-btn {
  @include button();
}
</style>
